'use client'

import { IconStarFill } from '@macarongfactory/web-icons'
import { ConfirmDialog, Text } from '@macarongfactory/web-ui'
import { useEffect, useRef } from 'react'

import {
  MODOOCAR_CODE,
  OWNER_RATING_LABEL,
  OWNER_RATING_MODEL_NAME_MAX_LENGTH,
  useModoocarTracking,
} from '../_features/modoocar'

interface IOwnerRatingDialogProps {
  open: boolean
  modelName: string
  score: number
  onScoreChange: (score: number) => void
  onClose: () => void
  onConfirm: () => void
  isSubmitting?: boolean
}

const TOTAL_STARS = 5
const STAR_ICON_SIZE_PX = 40

const OwnerRatingDialog = ({
  open,
  modelName,
  score,
  onScoreChange,
  onClose,
  onConfirm,
  isSubmitting = false,
}: IOwnerRatingDialogProps) => {
  const title =
    modelName.length >= OWNER_RATING_MODEL_NAME_MAX_LENGTH
      ? '내 차 얼마나 만족하시나요?'
      : `‘${modelName}’ 얼마나 만족하시나요?`

  const { itemShow, itemClick } = useModoocarTracking()
  const shownRef = useRef(false)
  useEffect(() => {
    if (!open || shownRef.current) return
    shownRef.current = true
    itemShow(
      MODOOCAR_CODE.SATISFACTION_RATING,
      'dialog',
      '오너 만족도 평가 안내'
    )
  }, [open, itemShow])

  const handleScoreChange = (value: number) => {
    itemClick(MODOOCAR_CODE.SATISFACTION_RATING, 'star', `${value}점`, {
      score: value,
    })
    onScoreChange(value)
  }

  return (
    <ConfirmDialog
      open={open}
      onClose={onClose}
      className='[&_section>div]:!pb-dimension-x4'
      top={
        <ConfirmDialog.Top.LeftAlignedHeader
          title={title}
          className='break-keep'
        />
      }
      content={
        <div className='flex flex-col items-center gap-dimension-x2 px-dimension-x5 py-dimension-x3'>
          <div
            role='radiogroup'
            aria-label='만족도 별점'
            className='flex gap-dimension-x0-5'
          >
            {Array.from({ length: TOTAL_STARS }, (_, index) => {
              const value = index + 1
              const isActive = value <= score
              return (
                <button
                  key={value}
                  type='button'
                  role='radio'
                  aria-checked={value === score}
                  aria-label={`${value}점`}
                  onClick={() => handleScoreChange(value)}
                  className={
                    isActive
                      ? 'text-foreground-caution-strong'
                      : 'text-foreground-neutral-muted'
                  }
                >
                  <IconStarFill
                    width={STAR_ICON_SIZE_PX}
                    height={STAR_ICON_SIZE_PX}
                  />
                </button>
              )
            })}
          </div>
          <div className='flex items-center gap-dimension-x1'>
            <Text
              variant='title-1-semibold'
              color='text-foreground-neutral-strong'
            >
              {score}점
            </Text>
            <Text
              variant='title-1-semibold'
              color='text-foreground-neutral-strong'
            >
              {OWNER_RATING_LABEL[score]}
            </Text>
          </div>
        </div>
      }
      bottom={
        <ConfirmDialog.Bottom.BottomCTA.Horizontal
          subButton={
            <ConfirmDialog.Bottom.SubButton.SolidButton
              text='취소'
              onClick={onClose}
            />
          }
          mainButton={
            <ConfirmDialog.Bottom.MainButton.SolidButton
              text='확인'
              onClick={onConfirm}
              isLoading={isSubmitting}
            />
          }
        />
      }
    />
  )
}

export default OwnerRatingDialog
